import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import useListService from '@/data/hook/useListService';
import { tagsQueryKeys } from '@/data/services/querykeys';
import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import { listAreaParents } from '@/data/services/areaServices';
import { extractParents, mountTopicTagName } from '@/utils/area';
import Tag, { SimpleTag, TagWithName } from '@/models/Tag';
import { uniqueId } from 'lodash';

type useTagsOptions = {
  value?: Array<number>;
};

async function fetchTagWithName(tag: Tag): Promise<SimpleTag> {
  const parentsRequest = await listAreaParents(tag.areaId);

  const parentsExtract = extractParents(parentsRequest.results.at(0));

  const result = {
    id: tag.id,
    name: mountTopicTagName(parentsExtract),
    description: tag.description,
  };

  return result;
}

export function useTags(options: useTagsOptions) {
  const { value = [] } = options;

  const { results: tags, isInitialLoading } = useListService({
    ...tagsQueryKeys.list({ id: value }),
    staleTime,
    enabled: !!value.length,
  });

  const queries = useQueries({
    queries: tags.map(tag => ({
      queryKey: [tag.id, tag.areaId],
      queryFn: () => fetchTagWithName(tag),
      staleTime,
      enabled: !!tag.areaId,
    })),
  });

  const mountedTags = useMemo(() => {
    const validQueries = queries.filter(query => query.data);

    const tagsData = validQueries.map(
      query =>
        new TagWithName({
          ...query.data!,
          tempId: uniqueId(),
        }),
    );

    return tagsData;
  }, [queries]);

  const loading = isInitialLoading || queries.some(query => query.isLoading);

  return {
    tags: mountedTags,
    loading,
  };
}
